'use client';

import { c as _c } from "react/compiler-runtime";
import type { Data } from '@repo/strapi-types';
import { useTranslations } from 'next-intl';
import type React from 'react';
import AppLink from '@/components/elementary/AppLink';
import { MobileLocaleSwitcher } from '@/components/elementary/LocaleSwitcher/MobileLocaleSwitcher';
import { CTAButtons } from '@/components/elementary/navbar/mobile/CTAButtons';
import { NavbarColumnGroup } from '@/components/elementary/navbar/NavbarColumnGroup';
import { NavbarMenuLink } from '@/components/elementary/navbar/NavbarMenuLink';
import { CaretRight } from '@/components/icons/CaretRight';
import { Paragraph } from '@/components/typography/Paragraph';
import { Button } from '@/components/ui/button';
import { usePathname } from '@/i18n/navigation';
import { ROOT_BLOG_PAGE_PATH } from '@/lib/constants';
import { cn } from '@/lib/styles';
import { NAVBAR_TEST_IDS } from '@/lib/test-ids';
import type { AppLocale } from '@/types/general';
import type { LocaleLink } from '@/types/i18n.types';
import { getBlogCategoryLink, getBlogLink } from '@/utils/links';
import styles from './MobileMenu.module.css';
interface MobileMenuProps {
  navbarData: Data.ContentType<'api::navbar.navbar'>;
  categories: Data.ContentType<'api::blog-category.blog-category'>[];
  isMobileMenuOpen: boolean;
  resetMobileMenu: () => void;
  activeNavMenuContent: Data.Component<'layout-utilities.navbar-item'> | null;
  setActiveNavMenuContent: (item: Data.Component<'layout-utilities.navbar-item'> | null) => void;
  isOpenDropDown: boolean;
  setIsOpenDropDown: (isOpen: boolean) => void;
  locale: AppLocale;
  localeLinks?: LocaleLink[];
  ref?: React.RefObject<HTMLElement | null>;
}
export const MobileMenu = t0 => {
  const $ = _c(38);
  const {
    navbarData,
    categories,
    isMobileMenuOpen,
    resetMobileMenu,
    activeNavMenuContent,
    setActiveNavMenuContent,
    isOpenDropDown,
    setIsOpenDropDown,
    locale,
    localeLinks,
    ref
  } = t0;
  const {
    items,
    dropdown,
    login,
    buttons
  } = navbarData;
  const t = useTranslations("MobileMenu");
  const t1 = usePathname();
  let t2;
  if ($[0] !== t1) {
    t2 = t1.startsWith(ROOT_BLOG_PAGE_PATH);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isBlogPage = t2;
  const showBlogCategories = isBlogPage && !isOpenDropDown && !activeNavMenuContent;
  const showDropdownMenuContent = dropdown && isOpenDropDown;
  const showNavMenuContent = (activeNavMenuContent?.columns?.length ?? 0) > 0;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function hasNavMenuContent(item) {
      return item.columns && item.columns.length > 0;
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const hasNavMenuContent = t3;
  const t4 = isMobileMenuOpen ? "translate-x-0 visible" : "translate-x-full";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("fixed top-(--site-header-offset-mobile) z-50 flex flex-col px-6 bg-neutral-light-100 overflow-y-scroll max-h-[calc(100dvh-var(--site-header-offset-mobile))] min-h-[calc(100dvh-var(--site-header-offset-mobile))] w-full lg:hidden divide-y divide-neutral-light-500", "transition-transform duration-500 ease-in-out", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== activeNavMenuContent || $[6] !== categories || $[7] !== dropdown || $[8] !== isOpenDropDown || $[9] !== items || $[10] !== resetMobileMenu || $[11] !== setActiveNavMenuContent || $[12] !== setIsOpenDropDown || $[13] !== showBlogCategories || $[14] !== showDropdownMenuContent || $[15] !== showNavMenuContent || $[16] !== t) {
    t6 = !activeNavMenuContent && !isOpenDropDown ? <ul className="flex flex-col">{items?.map(item_0 => !hasNavMenuContent(item_0) && item_0.href ? <li key={item_0.id}><AppLink link={item_0} className="flex items-center justify-between py-8 border-b border-neutral-light-500 w-full" onClick={resetMobileMenu}><Paragraph fontSize="displaySmall" className={cn("text-inherit", styles["menu-link"])}>{item_0.label}</Paragraph></AppLink></li> : <li key={item_0.id}><Button onClick={() => setActiveNavMenuContent(item_0)} variant="primaryLink" size="none" className="flex items-center justify-between py-8 border-b border-neutral-light-500 w-full"><Paragraph fontSize="displaySmall" className={cn("text-inherit", styles["menu-link"])}>{item_0.label}</Paragraph><CaretRight /></Button></li>)}{!isOpenDropDown && dropdown ? <li><Button onClick={() => setIsOpenDropDown(true)} variant="primaryLink" size="none" className="flex items-center justify-between py-8 w-full"><Paragraph fontSize="displaySmall" className={cn("text-inherit", styles["menu-link"])}>{dropdown?.label}</Paragraph><CaretRight /></Button></li> : null}{showBlogCategories ? <><li><AppLink href={getBlogLink()} className="flex items-center justify-between py-8  border-t border-b-0 border-neutral-light-500" onClick={resetMobileMenu}><Paragraph fontSize="displaySmall" className={cn("text-inherit", styles["menu-link"])}>{t("blogTitle")}</Paragraph></AppLink></li>{categories.map(category => {
          if (!category.slug) {
            return null;
          }
          return <li key={category.id}><AppLink href={getBlogCategoryLink(category.slug)} className="flex items-center justify-between py-8 border-t border-b-0 border-neutral-light-500" onClick={resetMobileMenu}><Paragraph fontSize="displaySmall" className={cn("text-inherit", styles["menu-link"])}>{category.name}</Paragraph></AppLink></li>;
        })}</> : null}</ul> : <>{showNavMenuContent ? <div className="divide-y divide-neutral-light-500">{activeNavMenuContent?.columns?.map(column => <div className="divide-y divide-neutral-light-500" key={`menuItem-${column.id}`}>{column.groups?.map((group, groupIndex) => <NavbarColumnGroup key={`categoryMobileCard-${group.id}`} group={group} columnTitle={groupIndex === 0 ? column.title : null} testId={NAVBAR_TEST_IDS.CATEGORY_COLUMN_MOBILE} className="py-8" onLinkClick={resetMobileMenu} />)}</div>)}</div> : null}{showDropdownMenuContent ? <ul className="divide-y divide-neutral-light-500">{dropdown.links?.map(link => <li key={link.id} className="py-8"><NavbarMenuLink link={link} isDropDownItem={true} onClick={resetMobileMenu} /></li>)}</ul> : null}</>;
    $[5] = activeNavMenuContent;
    $[6] = categories;
    $[7] = dropdown;
    $[8] = isOpenDropDown;
    $[9] = items;
    $[10] = resetMobileMenu;
    $[11] = setActiveNavMenuContent;
    $[12] = setIsOpenDropDown;
    $[13] = showBlogCategories;
    $[14] = showDropdownMenuContent;
    $[15] = showNavMenuContent;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = (activeNavMenuContent || isOpenDropDown) && !showDropdownMenuContent ? dropdown : null;
  const t8 = !activeNavMenuContent && !isOpenDropDown ? "menu-items" : "menu-content";
  let t9;
  if ($[18] !== setActiveNavMenuContent || $[19] !== setIsOpenDropDown) {
    t9 = () => {
      setIsOpenDropDown(true);
      setActiveNavMenuContent(null);
    };
    $[18] = setActiveNavMenuContent;
    $[19] = setIsOpenDropDown;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== buttons || $[22] !== login || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t10 = <CTAButtons login={login} buttons={buttons} dropdown={t7} navContext={t8} onDropdownClick={t9} />;
    $[21] = buttons;
    $[22] = login;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== activeNavMenuContent || $[28] !== isOpenDropDown || $[29] !== locale || $[30] !== localeLinks) {
    t11 = localeLinks && localeLinks.length > 1 && !activeNavMenuContent && !isOpenDropDown && <div className="py-8"><MobileLocaleSwitcher currentLocale={locale} localeLinks={localeLinks} /></div>;
    $[27] = activeNavMenuContent;
    $[28] = isOpenDropDown;
    $[29] = locale;
    $[30] = localeLinks;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== ref || $[33] !== t10 || $[34] !== t11 || $[35] !== t5 || $[36] !== t6) {
    t12 = <nav ref={ref} className={t5}>{t6}{t10}{t11}</nav>;
    $[32] = ref;
    $[33] = t10;
    $[34] = t11;
    $[35] = t5;
    $[36] = t6;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};